import React from 'react';

import { Menu, TextInput } from '@mantine/core';
import { Column } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronDown, Eye, EyeOff, Filter, X } from 'lucide-react';

interface ColumnMenuProps<TData> {
  column: Column<TData, unknown>;
  title: string;
  className?: string;
}

export function ColumnMenu<TData>({ column, title, className }: ColumnMenuProps<TData>) {
  const filterValue = (column.getFilterValue() as string) ?? '';
  const [filterInput, setFilterInput] = React.useState(filterValue);

  const handleFilterChange = (value: string) => {
    setFilterInput(value);
    column.setFilterValue(value || undefined);
  };

  const handleClearFilter = () => {
    setFilterInput('');
    column.setFilterValue(undefined);
  };

  return (
    <Menu>
      <Menu.Target>
        <button
          type="button"
          className={`inline-flex items-center gap-1 hover:bg-muted rounded px-1 py-0.5 transition-colors ${className || ''}`}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering sort on parent header
          }}
        >
          <span>{title}</span>
          <ChevronDown className="w-3 h-3" />
          {column.getIsFiltered() && <Filter className="w-3 h-3 text-primary" />}
        </button>
      </Menu.Target>
      <Menu.Dropdown
        className="w-64"
        onClick={(e) => {
          e.stopPropagation(); // Prevent clicks in dropdown from bubbling
        }}
      >
        {/* Filter Section */}
        <Menu.Label>Filter</Menu.Label>
        <div className="px-2 py-2">
          <TextInput
            placeholder={`Filter by ${title}...`}
            value={filterInput}
            onChange={(e) => handleFilterChange(e.target.value)}
            rightSection={
              filterInput && (
                <button type="button" onClick={handleClearFilter} className="text-gray-500 hover:text-gray-800 text-lg leading-none" aria-label="Clear filter">
                  ×
                </button>
              )
            }
            size="xs"
          />
        </div>

        {/* Clear Filter Option */}
        {column.getIsFiltered() && (
          <>
            <Menu.Item leftSection={<X className="w-4 h-4" />} onClick={handleClearFilter}>
              Clear filter
            </Menu.Item>
            <Menu.Divider />
          </>
        )}

        {/* Sort Section */}
        {column.getCanSort() && (
          <>
            <Menu.Label>Sort</Menu.Label>
            <Menu.Item leftSection={<ArrowUp className="w-4 h-4" />} onClick={() => column.toggleSorting(false)}>
              Sort ascending
            </Menu.Item>
            <Menu.Item leftSection={<ArrowDown className="w-4 h-4" />} onClick={() => column.toggleSorting(true)}>
              Sort descending
            </Menu.Item>
            {column.getIsSorted() && (
              <Menu.Item leftSection={<X className="w-4 h-4" />} onClick={() => column.clearSorting()}>
                Clear sort
              </Menu.Item>
            )}
            <Menu.Divider />
          </>
        )}

        {/* Visibility Section */}
        <Menu.Item leftSection={column.getIsVisible() ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} onClick={() => column.toggleVisibility()}>
          {column.getIsVisible() ? 'Hide' : 'Show'} column
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
